// assets/composables/useMovements.js
import { ref, computed } from 'vue'
import { useToast } from './useToast.js'

/**
 * @param {string|number} investmentId
 * @param {Function} getAuthHeader
 * @param {import('vue').Ref} investment - reactive ref to the investment object (for endDate virtual movement)
 */
export function useMovements(investmentId, getAuthHeader, investment = null) {
    const toast = useToast()

    const movements = ref([])
    const virtualMovements = ref([])
    const investmentCurrentValue = ref(0)
    const investmentInvestedAmount = ref(0)
    const loadingMovements = ref(false)
    const loadingSimulation = ref(false)

    const loadFromCache = () => {
        const cache = localStorage.getItem(`fin_movements_cache_${investmentId}`)
        if (cache) {
            const data = JSON.parse(cache)
            movements.value = data.movements
            investmentCurrentValue.value = parseFloat(data.investmentCurrentValue || 0)
            investmentInvestedAmount.value = parseFloat(data.investmentInvestedAmount || 0)

            const simCache = localStorage.getItem(`fin_simulation_cache_${investmentId}`)
            if (simCache) {
                virtualMovements.value = JSON.parse(simCache).virtualMovements || []
            }
        }
    }

    const fetchMovements = async () => {
        loadingMovements.value = movements.value.length === 0
        try {
            const res = await fetch(`/api/movements/investment/${investmentId}`, { headers: getAuthHeader() })
            if (res.ok) {
                const data = await res.json()
                movements.value = data.movements
                investmentCurrentValue.value = parseFloat(data.investmentCurrentValue || 0)
                investmentInvestedAmount.value = parseFloat(data.investmentInvestedAmount || 0)
                
                // Save to cache
                localStorage.setItem(`fin_movements_cache_${investmentId}`, JSON.stringify(data))

                // Fetch virtual movements from backend simulation engine
                loadingSimulation.value = true
                const simRes = await fetch(`/api/investments/${investmentId}/simulation`, { headers: getAuthHeader() })
                if (simRes.ok) {
                    const simData = await simRes.json()
                    virtualMovements.value = simData.virtualMovements || []
                    localStorage.setItem(`fin_simulation_cache_${investmentId}`, JSON.stringify(simData))
                }
                loadingSimulation.value = false
            }
        } catch (e) { console.error(e) }
        finally {
            loadingMovements.value = false
            loadingSimulation.value = false
        }
    }

    const validateMovement = async (id) => {
        try {
            const res = await fetch(`/api/movements/${id}/validate`, { method: 'POST', headers: getAuthHeader() })
            if (res.ok) fetchMovements()
        } catch (e) { console.error(e) }
    }

    const deleteMovement = async (id, deleteGroup) => {
        try {
            const res = await fetch(`/api/movements/${id}?deleteGroup=${deleteGroup}`, { method: 'DELETE', headers: getAuthHeader() })
            if (res.ok) fetchMovements()
        } catch (e) { console.error(e) }
    }

    const clearHistory = async (investmentId) => {
        try {
            const res = await fetch(`/api/movements/investment/${investmentId}`, { method: 'DELETE', headers: getAuthHeader() })
            if (res.ok) {
                fetchMovements()
                return true
            }
        } catch (e) {
            toast.error("Erreur serveur lors de la suppression de l'historique.")
        }
        return false
    }

    const deleteImportBatch = async (importId) => {
        try {
            const res = await fetch(`/api/movements/import/${importId}`, { method: 'DELETE', headers: getAuthHeader() })
            if (res.ok) {
                fetchMovements()
                return true
            } else {
                toast.error("Erreur lors de la suppression.")
            }
        } catch (e) {
            toast.error("Erreur réseau lors de la suppression.")
        }
        return false
    }

    // --- Filtering ---
    const selectedFilterCategories = ref([])
    const selectedFilterYear = ref(null)

    const availableYears = computed(() => {
        const years = new Set()
        movements.value.forEach(m => years.add(m.date.substring(0, 4)))
        // Also add years from virtual movements
        virtualMovements.value.forEach(m => years.add(m.date.substring(0, 4)))
        return Array.from(years).sort((a, b) => b - a)
    })

    const isYearSelected = (m) => {
        if (!selectedFilterYear.value) return true
        return m.date.substring(0, 4) === selectedFilterYear.value
    }

    const isCategorySelected = (m) => {
        if (selectedFilterCategories.value.length === 0) return true
        const selectedIds = selectedFilterCategories.value.map(c => c.id)
        if (!m.subMovements || m.subMovements.length === 0) {
            if (!m.category) return false
            return selectedIds.includes(m.category.id)
        }
        return m.subMovements.some(s => s.category && selectedIds.includes(s.category.id))
    }

    const rawRealizedMovements = computed(() => movements.value.filter(m => m.isRealized))
    const realizedMovements = computed(() => {
        return rawRealizedMovements.value
            .filter(m => isCategorySelected(m) && isYearSelected(m))
            .sort((a, b) => new Date(b.date) - new Date(a.date))
    })

    /**
     * Future movements including a virtual "end date" receipt movement
     * if the investment has an endDate set and currentValue > 0.
     */
    const futureMovements = computed(() => {
        const list = [...movements.value.filter(m => !m.isRealized && isCategorySelected(m) && isYearSelected(m))]

        // Inject virtual movements from backend (e.g. liquidation)
        // Note: we don't apply category filter to virtual movements for now 
        // as they are usually "Placement" category which isn't in the movement categories list
        if (virtualMovements.value.length > 0) {
            list.push(...virtualMovements.value.filter(m => isYearSelected(m)))
        }

        return list.sort((a, b) => new Date(a.date) - new Date(b.date))
    })

    const groupByYear = (list, sortDir = 'desc') => {
        const groups = {}
        list.forEach(m => {
            const year = m.date.substring(0, 4)
            if (!groups[year]) groups[year] = []
            groups[year].push(m)
        })
        return Object.keys(groups)
            .sort((a, b) => sortDir === 'desc' ? b - a : a - b)
            .map(year => ({ year, movements: groups[year] }))
    }

    const realizedGroups = computed(() => groupByYear(realizedMovements.value, 'desc'))
    const futureGroups = computed(() => groupByYear(futureMovements.value, 'asc'))

    // --- Category sums (for chart table) ---
    const categorySums = computed(() => {
        const sums = {}
        realizedMovements.value.forEach(m => {
            if (m.subMovements && m.subMovements.length > 0) {
                m.subMovements.forEach(sub => {
                    const catName = sub.category ? sub.category.name : 'Sans catégorie'
                    if (!sums[catName]) sums[catName] = { payment: 0, receipt: 0 }
                    if (sub.type === 'payment') sums[catName].payment += parseFloat(sub.amount)
                    else sums[catName].receipt += parseFloat(sub.amount)
                })
            } else {
                const catName = m.category ? m.category.name : 'Sans catégorie'
                if (!sums[catName]) sums[catName] = { payment: 0, receipt: 0 }
                if (m.type === 'payment') sums[catName].payment += parseFloat(m.amount)
                else sums[catName].receipt += parseFloat(m.amount)
            }
        })
        return Object.keys(sums).map(catName => ({
            name: catName,
            payment: sums[catName].payment,
            receipt: sums[catName].receipt,
            net: sums[catName].receipt - sums[catName].payment
        })).sort((a, b) => b.payment - a.payment)
    })

    return {
        movements, investmentCurrentValue, investmentInvestedAmount,
        loadingMovements, loadingSimulation,
        fetchMovements, loadFromCache, validateMovement, deleteMovement, clearHistory, deleteImportBatch,
        selectedFilterCategories, selectedFilterYear, availableYears,
        rawRealizedMovements, realizedMovements, futureMovements,
        realizedGroups, futureGroups, categorySums,
    }
}
